import {
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
} from "@mui/material";
import { useIntl } from "react-intl";
import { DropzoneAreaBase, FileObject } from "mui-file-dropzone";
import { useEffect, useRef, useState } from "react";
import useGlobalContext from "hooks/globalData";
import translateError from "utils/translateError";
import { createSimulatedProgress } from "utils/simulatedProgress";
import MediaLibraryItemPreview from "pages/MediaLibraryPage/MediaLibraryItemPreview";
import { IQuestion, ResponseCode } from "services/KnowfullyAdminClient";

const ACCEPTED_FILE_TYPES = [".docx", ".md", ".doc"];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

type ImportState = {
  fileObject: FileObject | null;
  error: string | null;
  progress: number;
  isUploading: boolean;
};

const initialImportState: ImportState = {
  fileObject: null,
  error: null,
  progress: 0,
  isUploading: false,
};

export type EvaluationImportDialogProps = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onImport?: (questions: IQuestion[]) => void;
};

export function EvaluationImportDialog({
  open,
  onClose,
  onImport,
}: Readonly<EvaluationImportDialogProps>) {
  const { formatMessage: t } = useIntl();
  const [state, setState] = useState<ImportState>(initialImportState);
  const { evaluationService } = useGlobalContext();
  const progressController = useRef<ReturnType<
    typeof createSimulatedProgress
  > | null>(null);

  const resetState = () => {
    progressController.current?.stop();
    setState(initialImportState);
  };

  useEffect(() => {
    if (open) {
      resetState();
    }
  }, [open]);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleFileAdd = async (files: FileObject[]) => {
    const file = files[0];
    if (!file) return;

    setState({ fileObject: file, error: null, isUploading: true, progress: 0 });

    progressController.current = createSimulatedProgress((progress) =>
      setState((prev) => ({ ...prev, progress }))
    );
    progressController.current.start();

    try {
      const response = await evaluationService.importQuestions({
        data: file.file,
        fileName: file.file.name,
      });

      if (response.responseCode === ResponseCode.OK && response.data) {
        progressController.current?.stop();
        setState((prev) => ({ ...prev, progress: 100 }));
        onImport?.(response.data);
      } else {
        progressController.current?.stop();
        setState((prev) => ({
          ...prev,
          isUploading: false,
          error: response.responseReason ?? "Import failed",
        }));
      }
    } catch (err: any) {
      progressController.current?.stop();
      setState((prev) => ({
        ...prev,
        isUploading: false,
        error: translateError(err, t),
      }));
    }
  };

  const { fileObject, error, progress, isUploading } = state;

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={handleClose}>
      <DialogTitle>{t({ id: "importButton" })}</DialogTitle>

      <DialogContent>
        {fileObject && (
          <Grid container justifyContent="center">
            <MediaLibraryItemPreview
              fileName={fileObject.file.name}
              fileSize={fileObject.file.size}
              contentType={fileObject.file.type}
              onClose={() =>
                setState((prev) => ({ ...prev, fileObject: null, error: null }))
              }
            />
          </Grid>
        )}
        {!fileObject && (
          <DropzoneAreaBase
            acceptedFiles={ACCEPTED_FILE_TYPES}
            maxFileSize={MAX_FILE_SIZE}
            filesLimit={1}
            onAdd={handleFileAdd}
            fileObjects={[]}
            showAlerts={["error", "info"]}
            dropzoneProps={{
              disabled: isUploading,
            }}
          />
        )}
        {error && (
          <Alert sx={{ width: "100%", mt: 2 }} severity="error">
            {error}
          </Alert>
        )}
      </DialogContent>

      {isUploading && (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ height: 20 }}
        />
      )}
    </Dialog>
  );
}
